import { Text, View, Link } from "@react-pdf/renderer";
import { Order } from "models/Order";
import PageLayout from "../components/PageLayout";
import SectionHeader from "../components/SectionHeader";
import InspectionsSection from "../components/InspectionsSection";
import { color } from "../styles";
import Header from "../components/Header";
import PageContent from "../components/PageContent";
import QrCode from "../graphics/QrCode";
import { useLocalization } from "../localization/LocalizationContext";

type OrderPageProps = {
  order: Order;
};

const OrderPage: React.FC<OrderPageProps> = ({ order }) => {
  const { strings } = useLocalization();
  const t = strings.orderPage;

  return (
    <PageLayout>
      <Header title={t.reportTitle} />

      <PageContent style={{ paddingTop: 20, flexGrow: 1 }}>
        <View>
          <SectionHeader>
            <Text>{t.orderSection}</Text>
          </SectionHeader>

          <View
            style={{
              paddingVertical: 15,
              paddingHorizontal: 20,
              gap: 10,
              backgroundColor: color.lightGrey,
            }}
          >
            <View
              style={{
                display: "flex",
                flexDirection: "row",
                gap: 5,
                fontSize: 12,
              }}
            >
              <Text style={{ fontWeight: "bold" }}>{t.orderNumber}:</Text>
              <Text>
                {[order.ordernr, ...(order.projectOrders ?? [])].map((order) => order).join(", ")}
              </Text>
            </View>

            <View
              style={{
                display: "flex",
                flexDirection: "row",
                gap: 5,
                fontSize: 12,
              }}
            >
              <Text style={{ fontWeight: "bold" }}>{t.customer}:</Text>
              <Text>{order.companyName}</Text>
            </View>

            <View
              style={{
                display: "flex",
                flexDirection: "row",
                gap: 5,
                fontSize: 12,
              }}
            >
              <Text style={{ fontWeight: "bold" }}>{t.address}:</Text>
              <Text>
                {order.address}, {order.postnr} {order.postaddress}
              </Text>
            </View>

            <View
              style={{
                display: "flex",
                flexDirection: "row",
                gap: 5,
                fontSize: 12,
              }}
            >
              <Text style={{ fontWeight: "bold" }}>{t.installations}:</Text>
              <Text>
                {order.checklists
                  .filter((checklist) => !checklist.excludedFromReport)
                  .map((checklist) => checklist.roomName)
                  .join(", ")}
              </Text>
            </View>
          </View>
        </View>

        {order.customerApprovals.length === 1 && (
          <InspectionsSection inspections={order.customerApprovals} />
        )}

        <View style={{ marginTop: "auto" }}>
          <SectionHeader>
            <Text>{t.serviceRequest.title}</Text>
          </SectionHeader>

          <View
            style={{
              paddingLeft: 20,
              paddingRight: 15,
              paddingVertical: 15,
              backgroundColor: color.lightGrey,
            }}
          >
            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: 12,
              }}
            >
              <View>
                <Text>
                  {t.serviceRequest.createServiceRequest}{" "}
                  <Link src={t.serviceRequest.url} style={{ color: color.black }}>
                    {t.serviceRequest.clickHere}
                  </Link>
                </Text>
                <Text style={{ marginTop: 5 }}>{t.serviceRequest.scanQrToSimplify}</Text>
              </View>

              <QrCode text={t.serviceRequest.url} width={40} height={40} fill={color.darkGrey} />
            </View>
          </View>
        </View>

        <Text
          style={{
            fontSize: 9,
            textAlign: "center",
            color: color.darkGrey,
            maxWidth: 340,
            marginVertical: 5,
            marginHorizontal: "auto",
          }}
        >
          {t.companyDescription}
        </Text>

        <View
          style={{
            width: 380,
            marginHorizontal: "auto",
            borderBottom: `1px solid ${color.grey}`,
          }}
        />
      </PageContent>
    </PageLayout>
  );
};

export default OrderPage;
